import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * BÖY SAUCE — application d'une proposition de l'optimiseur de panier.
 *
 * Le snippet boy-upsell-drawer calcule la meilleure composition possible et
 * pose sur cet élément la composition CIBLE complète dans data-updates, sous la
 * forme {"<id de variante>": <quantité voulue>}. On envoie ces quantités
 * absolues à /cart/update.js : les lignes non citées ne bougent pas, donc un
 * futur produit BÖY non recomposable reste intact dans le panier. Et parce que
 * ce sont des quantités absolues, une montée en format remplace réellement la
 * ligne d'origine : elle ne peut pas ajouter le nouveau pack en laissant
 * l'ancien.
 *
 * MESURE — l'événement « suggestion affichée » vit ici, et pas dans le snippet.
 * Deux causes se cumulaient, et la seconde n'était pas dans l'audit : une balise
 * script en ligne réinjectée par l'API de rendu de section ne s'exécute jamais,
 * ET le tiroir n'est pas remplacé mais FUSIONNÉ — après un changement de
 * quantité, le même élément survit, seuls ses attributs et son contenu changent.
 * Mesuré le 08/09 : un marqueur posé sur l'élément a survécu à deux changements
 * de quantité. connectedCallback ne repassant pas, sortir le code du snippet
 * n'aurait rien réparé au-delà de la première suggestion. On surveille donc les
 * attributs de mesure : chaque nouvelle suggestion réarme le témoin. Un envoi
 * par suggestion, et seulement quand la carte entre réellement dans le champ —
 * le tiroir existe fermé dès le premier chargement. Constat C-11.
 *
 * Trois garde-fous :
 *   — un seul envoi à la fois (data-loading, doublé d'un pointer-events: none) ;
 *   — aucune réussite annoncée tant que le serveur n'a pas répondu : le suivi
 *     analytique ne part qu'après la réponse ;
 *   — un échec se voit à l'écran, en français, et le bouton redevient cliquable.
 *
 * L'ancien contrat (data-add-variant / data-remove-variant) reste supporté.
 * Rien ne se produit sans clic du client.
 *
 * Fichier custom BÖY — ne fait pas partie de Savor.
 */
class BoyUpsellSwap extends HTMLElement {
  static get observedAttributes() {
    return ['data-rule', 'data-from-bottles', 'data-to-bottles', 'data-delta-paid'];
  }

  /** @type {IntersectionObserver | null} */
  #temoin = null;
  #vu = false;
  #reArmement = false;

  connectedCallback() {
    // Délégation sur l'élément lui-même : la fusion peut remplacer le bouton,
    // jamais l'élément. L'écouteur ne peut donc pas se perdre en route.
    this.addEventListener('click', this.#surClic);
    this.#armerTemoin();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#surClic);
    this.#temoin?.disconnect();
    this.#temoin = null;
  }

  /** Une nouvelle suggestion dans le même élément mérite sa propre mesure. */
  attributeChangedCallback(nom, ancienne, nouvelle) {
    if (ancienne === null || ancienne === nouvelle) return;
    this.#vu = false;
    if (this.#reArmement) return;
    this.#reArmement = true;
    queueMicrotask(() => {
      this.#reArmement = false;
      this.#armerTemoin();
    });
  }

  #surClic = (event) => {
    if (event.target instanceof Element && event.target.closest('[data-boy-swap]')) this.#swap();
  };

  /**
   * « Affichée » veut dire vue, pas rendue. On attend que la carte entre
   * réellement dans le champ, une seule fois. Sans IntersectionObserver, on
   * préfère un comptage large à un silence.
   */
  #armerTemoin() {
    if (this.#vu || !this.dataset.rule) return;

    this.#temoin?.disconnect();
    this.#temoin = null;

    if (typeof IntersectionObserver !== 'function') {
      this.#marquerVue();
      return;
    }

    this.#temoin = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.#marquerVue();
      },
      { threshold: 0.5 }
    );
    this.#temoin.observe(this);
  }

  #marquerVue() {
    if (this.#vu) return;
    this.#vu = true;
    this.#temoin?.disconnect();
    this.#temoin = null;
    this.#track('boy_step_shown');
  }

  /** Construit la carte des quantités absolues à appliquer. */
  #buildUpdates() {
    const raw = this.dataset.updates;
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch (error) {
        console.warn('[boy-escalier] data-updates illisible :', error);
      }
    }

    const addVariant = this.dataset.addVariant;
    if (!addVariant) return null;

    /** @type {Record<string, number>} */
    const updates = {};
    const removeVariant = this.dataset.removeVariant;
    if (removeVariant) updates[removeVariant] = 0;
    updates[addVariant] = parseInt(this.dataset.addQuantity || '1', 10);
    return updates;
  }

  /**
   * Un événement, deux destinations : le gestionnaire de pixels de Shopify, seule
   * voie qui applique le consentement du visiteur, et un événement DOM pour nos
   * propres contrôles. Plus rien n'est écrit dans dataLayer : aucun conteneur ne
   * le lit, et une plomberie morte finit par se faire prendre pour une mesure.
   */
  #track(name) {
    const payload = {
      event: name,
      rule: this.dataset.rule || null,
      from_bottles: Number(this.dataset.fromBottles) || null,
      to_bottles: Number(this.dataset.toBottles) || null,
      delta_paid: Number(this.dataset.deltaPaid) || 0,
      delta_products: Number(this.dataset.deltaProducts) || 0,
      crosses_threshold: this.dataset.crosses === 'true',
      surface: this.dataset.surface || 'drawer',
    };

    try {
      window.Shopify?.analytics?.publish?.(name, payload);
    } catch (error) {
      console.warn('[boy-escalier] publication analytique impossible :', error);
    }

    document.dispatchEvent(new CustomEvent(name, { detail: payload }));
  }

  /** Un échec ne doit jamais être silencieux : le client doit savoir que son panier n'a pas bougé. */
  #showError() {
    let box = this.querySelector('[data-boy-swap-error]');
    if (!box) {
      box = document.createElement('p');
      box.className = 'boy-upsell__erreur';
      box.setAttribute('data-boy-swap-error', '');
      box.setAttribute('role', 'alert');
      this.append(box);
    }
    box.textContent = "Le changement n'a pas pu être appliqué. Ton panier n'a pas bougé — réessaie.";
  }

  #clearError() {
    this.querySelector('[data-boy-swap-error]')?.remove();
  }

  #swap = async () => {
    if (this.hasAttribute('data-loading')) return;

    const updates = this.#buildUpdates();
    if (!updates) return;

    const sectionId =
      this.closest('cart-items-component')?.dataset.sectionId || this.dataset.sectionId || 'cart-drawer-section';

    this.#clearError();
    this.setAttribute('data-loading', '');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch('/cart/update.js', fetchConfig('json', {
        body: JSON.stringify({
          updates,
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      }));
      const data = await response.json();

      if (!response.ok || data.errors) {
        throw new Error(data.errors || data.description || 'Cart update failed');
      }

      this.#track('boy_step_taken');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          sections: data.sections,
          source: 'boy-escalier',
          didError: false,
        },
      });
    } catch (error) {
      console.warn('[boy-escalier] application de la proposition impossible :', error);
      this.#track('boy_step_failed');
      this.#showError();
      deferred.reject(error);
      this.removeAttribute('data-loading');
    }
  };
}

if (!customElements.get('boy-upsell-swap')) {
  customElements.define('boy-upsell-swap', BoyUpsellSwap);
}
